import axios, { AxiosRequestConfig } from 'axios'
import { normalizeDataForApi } from 'utils-next/api'

import { generateApiRoot, getAuthHeaders, networkErrorMapper } from './api.config'

export type CreateApiHandler = {
  callApi: () => undefined
}

export type CreateApiHandlerConfig = {
  requestWaitTimes?: number[]
  baseURL?: string
  extraHeaders?: Record<string, any>
  transformResponse?: (data: any) => any
}

export type ApiCaller = CreateApiHandler['callApi']

export function createApiHandler(
  token?: string,
  { baseURL = generateApiRoot(), extraHeaders = {}, transformResponse }: CreateApiHandlerConfig = {}
) {
  const transformRequest = [normalizeDataForApi, ...(axios.defaults.transformRequest as any)]
  const axiosInstance = axios.create({
    headers: { ...getAuthHeaders(token), ...extraHeaders },
    transformRequest,
    baseURL,
  })

  async function callApi<ReturnType>(config: AxiosRequestConfig): Promise<ReturnType> {
    try {
      const response = await axiosInstance.request<ReturnType>(config)
      if (transformResponse) {
        return transformResponse(response.data)
      } else {
        return response.data
      }
    } catch (error) {
      throw networkErrorMapper(error)
    }
  }

  async function callApiRaw<ReturnType>(config: AxiosRequestConfig) {
    return axiosInstance.request<ReturnType>(config)
  }

  async function downloadFile<ReturnType extends string>(fileName: string, config: AxiosRequestConfig) {
    try {
      const responseData = await callApi<ReturnType>(config)

      const url = window.URL.createObjectURL(new Blob([responseData]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)

      // Append to html link element page
      document.body.appendChild(link)

      // Start download
      link.click()

      // Clean up and remove the link
      link.parentNode?.removeChild(link)
    } catch (error) {
      throw networkErrorMapper(error)
    }
  }

  return {
    callApi,
    callApiRaw,
    downloadFile,
  }
}
